/**
 * ONE DECISION, IN FULL — the "Why this one was held" and "Approved — the
 * whole chain" artboards, which are one screen.
 *
 * They are one screen because they are the same screen: back link, the
 * reference as the heading, the verdict as a pill, the rule trace, a sidebar,
 * and actions. What differs is which blocks have anything to say. A held
 * decision has a reason and three buttons and no post-approval chain; an
 * approved one has the chain and no reason. Built as two files they would have
 * drifted on the first change to either, and the blocks they share are most of
 * both.
 *
 * It replaces the expandable `DecisionRow`. Everything that row said is said
 * here — the two badges, the disagreement sentence, the failed-follow-up
 * alarm, the features with their dashes, the demand-context copy — with the
 * room to say it properly.
 *
 * ── THE THREE BUTTONS ──
 *
 * "Approve anyway" and "Reject" are the REVIEW GRID'S OWN ACTIONS, through
 * ../syncQueue. The decisions API is read-only and no new endpoint was
 * invented: approving opens the same OrderInputModal the grid's "Review"
 * button opens, and saving it posts the same `flow: 'APPROVED'`. That is why
 * approving is two steps here as it is there — the order form is where a
 * person fixes what the partner got wrong, and skipping it would be a
 * different, worse action wearing the same label.
 *
 * Both are disabled when the offer has left the sync queue, because the post
 * needs a `StateHash` that only a queue row carries. The screen says which of
 * the two reasons applies rather than showing a button that cannot work.
 *
 * "Ask the agent" opens the real chat, scoped to this reference. The chat
 * agent has a `sync_decisions(reference=…)` tool and "why was N3115296 held?"
 * is the documented example for it, so this is a question it can actually
 * answer rather than a prompt dressed up as one. It is READ-ONLY there: the
 * agent can explain a hold and cannot release one, which is what the other
 * two buttons are for.
 */

import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Badge, Button } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import Error from '../../../Components/Error';
import { ChatPanel } from '../../../Components/ChatWidget';
import OrderInputModal from '../../Orders/Components/OrderInputModal';
import { fetchPartnerDecisions } from '../../../Services/PartnerIntegrationService';
import PartnersService from '../../../Services/PartnersService';
import {
  formatFractionAsPct,
  formatMarginPct,
  formatMoneyOrDash,
  formatNumberOrDash,
  isAbsent,
  prettifyToken,
  profitToneFor,
} from '../presentation';
import {
  ADVISORY_RULE_COPY,
  DEMAND_UNKNOWN_COPY,
  decidedByLabel,
  decisionHeadline,
  failedPostSteps,
  heldReasonCopy,
  outcomeSentence,
  partnerDocsCopy,
  postStepFailureCopy,
  postStepName,
  resolutionBadge,
  ruleOutcome,
} from '../decisionPresentation';
import {
  DECLINE_CONFIRMATION, SYNC_FLOW, fetchSyncQueue, findQueueRow, postSyncDecision,
} from '../syncQueue';
import {
  advisoryRules, byDecidedAtDesc, partnerNameFor, settlingRule, shortfallOf,
} from './decisionFacts';
import AgentReview from './Components/AgentReview';
import PolicyInForce from './Components/PolicyInForce';
import { formatDateTime } from '../../../Utils/timezone';

const dashIfBlank = (value) => {
  if (isAbsent(value)) return '—';
  return String(value);
};

/**
 * The rule trace as the design draws it: a tick or a cross, the rule in
 * words, what it was measured against, and the value it saw.
 *
 * Values are printed VERBATIM. The contract does not say what unit a rule's
 * `value`/`threshold` are in — a margin rule's −0.77 against a −0.05 floor
 * reads correctly as a pair of raw numbers, and guessing at a percentage is
 * the 100× error the whole tab is written to avoid. The one number specified
 * as a fraction is `features.margin`, and it goes through `formatMarginPct`.
 *
 * THREE OUTCOMES, NOT TWO. A rule the partner's policy stood down was
 * evaluated, was recorded and could not have refused, so it gets neither the
 * tick nor the cross: a green tick beside a standing-down margin rule reads
 * as "the offer cleared the floor", which on a rate-sheet approval at a
 * negative margin is exactly backwards. `ruleOutcome` owns that vocabulary
 * and the log shares it, rather than it being spelled a second way here.
 */
function RuleTrace({ rules }) {
  if (!Array.isArray(rules) || rules.length === 0) {
    return <p className="sync-decision-empty mb-0">No rules were recorded for this decision.</p>;
  }

  return (
    <ul className="auto-approval-checks">
      {rules.map((rule, index) => {
        const outcome = ruleOutcome(rule);
        return (
          <li
            // A trace can repeat a rule id across stages, so the index is part
            // of the identity; the list is render-only and never reordered.
            // eslint-disable-next-line react/no-array-index-key
            key={`${rule?.id ?? 'rule'}-${index}`}
            className={outcome.kind === 'pass' ? undefined : `is-${outcome.kind}`}
          >
            <span className={`auto-approval-check-dot is-${outcome.kind}`}>
              <i className={`fa ${outcome.icon}`} aria-hidden="true" />
              <span className="visually-hidden">{outcome.announced}</span>
            </span>
            <span className="auto-approval-check-main">
              <span className="auto-approval-check-rule">{prettifyToken(rule?.id, 'Unnamed rule')}</span>
              <span className="auto-approval-check-detail">
                <code>{dashIfBlank(rule?.id)}</code>
                {!isAbsent(rule?.threshold) && ` · measured against ${rule.threshold}`}
                {/* Why there is no threshold on this line, for the one case
                    where its absence is a policy and not a gap. */}
                {outcome.kind === 'advisory' && ' · recorded, did not gate'}
              </span>
            </span>
            <span className={`auto-approval-check-value is-${outcome.kind}`}>
              {/* A rule that measured nothing still has an answer. `value` and
                  `threshold` are genuinely absent on the rules that only
                  check a condition (partner enabled, not a duplicate), so the
                  cell says what became of the rule rather than dashing a
                  measurement nobody was taking. */}
              {isAbsent(rule?.value) ? outcome.word : String(rule.value)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "What approval set in motion" — the post-approval chain, numbered.
 *
 * The design puts a code location (`where`) on every step. A post step
 * records `id`, `ok`, `detail` and `ms` and no location, so the step's own
 * name stands in and `detail` carries the body. The design's fifth step
 * ("operator still unassigned") is not a recorded step at all and is not
 * invented here.
 */
function PostSteps({ steps }) {
  if (!Array.isArray(steps) || steps.length === 0) {
    return <p className="sync-decision-empty mb-0">Nothing ran after this decision.</p>;
  }

  return (
    <ol className="auto-approval-timeline">
      {steps.map((step, index) => (
        <li
          // eslint-disable-next-line react/no-array-index-key
          key={`${step?.id ?? 'step'}-${index}`}
          className={step?.ok === false ? 'is-failed' : undefined}
        >
          <span className="auto-approval-timeline-mark">{index + 1}</span>
          <span className="auto-approval-timeline-body">
            <span className="auto-approval-timeline-title">{postStepName(step?.id)}</span>
            {/* The step's own detail — the error message when it failed.
                What a failed step MEANS is said once, in the alarm panel
                above, which is where it has to be read without scrolling. */}
            <span className="auto-approval-timeline-detail">
              {dashIfBlank(step?.detail)}
            </span>
          </span>
          <span className="auto-approval-timeline-ms">
            {formatNumberOrDash(step?.ms, (ms) => `${ms} ms`)}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Our quote, their offer, and the gap between them. */
function CostOfBeingWrong({ features }) {
  const gap = shortfallOf(features);

  return (
    <div className="auto-approval-money">
      <div>
        <span className="auto-approval-money-label">Our quote</span>
        <span className="auto-approval-money-value">
          {formatMoneyOrDash(features.our_price, features.currency)}
        </span>
      </div>
      <div>
        <span className="auto-approval-money-label">Their offer</span>
        <span className="auto-approval-money-value">
          {formatMoneyOrDash(features.offer, features.currency)}
        </span>
      </div>
      <div>
        <span className="auto-approval-money-label">Shortfall</span>
        <span className={`auto-approval-money-value ${gap !== null && gap < 0 ? 'is-bad' : ''}`}>
          {formatMoneyOrDash(gap, features.currency)}
        </span>
      </div>
    </div>
  );
}

function DecisionDetail() {
  const { reference } = useParams();
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [queueRow, setQueueRow] = useState(null);
  const [queueChecked, setQueueChecked] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [acted, setActed] = useState('');
  const [partners, setPartners] = useState([]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const decisions = await fetchPartnerDecisions({ reference });
      setRows(Array.isArray(decisions?.rows) ? decisions.rows : []);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [reference]);

  useEffect(() => { load(); }, [load]);

  /**
   * The partner list, for the one thing `partner_name` cannot be trusted for.
   * `SyncDecisionService` writes the partner UUID into that field for any
   * partner outside the policy table — KiwiTaxi, i.e. most held offers — so
   * without this the heading of the busiest drill-down reads as a UUID. A
   * failure costs the name and nothing else.
   */
  useEffect(() => {
    let live = true;
    PartnersService.fetchPartners()
      .then((response) => { if (live) setPartners(response?.data?.data || []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // The queue read is separate and its failure is swallowed: it decides only
  // whether the two action buttons work, and a decision trace is worth
  // reading whether or not the offer can still be acted on.
  useEffect(() => {
    let live = true;
    fetchSyncQueue()
      .then((queue) => { if (live) setQueueRow(findQueueRow(queue, reference)); })
      .catch(() => {})
      .finally(() => { if (live) setQueueChecked(true); });
    return () => { live = false; };
  }, [reference]);

  // Newest first, and the newest is the one shown. One reference can carry
  // several decisions — a pass re-scores an offer nobody has acted on, and a
  // pass is started by every arriving offer plus a daily safety-net sweep, so
  // a row that sits gets looked at again — so the count is said out loud
  // rather than the older ones being hidden.
  const sorted = useMemo(() => [...rows].sort(byDecidedAtDesc), [rows]);
  const row = sorted[0];

  const stateHash = queueRow?.Metadata?.state_hash;
  const order = queueRow?.Data;
  const canAct = Boolean(order && stateHash) && !acted;

  const approved = useCallback(async () => {
    try {
      await postSyncDecision({ order, flow: SYNC_FLOW.APPROVED, stateHash });
      setActed('approved');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }, [order, stateHash, load]);

  const reject = useCallback(async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(DECLINE_CONFIRMATION)) return;
    try {
      await postSyncDecision({ order, flow: SYNC_FLOW.DECLINED, stateHash });
      setActed('declined');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }, [order, stateHash, load]);

  /**
   * The reference and the facts of the decision, in the words of the prompt.
   *
   * The agent's wire protocol is a bare `{prompt}`, so there is no field to
   * put a reference in — scoping happens in the text, exactly as
   * KnowledgeBaseAsk does it. The facts are included because the agent's
   * lookup is capped and a question it can answer from the preamble is a
   * question answered in one turn; it still has the tool for everything the
   * preamble does not carry.
   */
  const transformMessage = useCallback((text) => {
    if (!row) return text;
    const features = row.features || {};
    const partnerName = partnerNameFor(row, partners);
    return [
      `[About partner-sync offer ${row.reference} (${partnerName}).`,
      ` The policy recorded "${row.decision}"`,
      row.held_reason ? ` with held reason "${row.held_reason}"` : '',
      '. Use sync_decisions(reference="',
      row.reference,
      '") for the full trace. For context: margin ',
      isAbsent(features.margin) ? 'was not measurable' : String(features.margin),
      ', our price ',
      isAbsent(features.our_price) ? 'not computed' : String(features.our_price),
      ', their offer ',
      isAbsent(features.offer) ? 'not recorded' : String(features.offer),
      '.]\n\n',
      text,
    ].join('');
  }, [row, partners]);

  if (isLoading) {
    return (
      <div className="partner-sync auto-approval">
        <div className="text-center my-5 py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3 mb-0">Loading this decision…</p>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="partner-sync auto-approval">
        <Link className="auto-approval-back" to="/partner-sync">← Back to the queue</Link>
        <Error error={error} setError={setError} />
        <div className="sync-empty">
          <div className="sync-empty-icon"><i className="fa fa-circle-question" /></div>
          <h4 className="fw-bold mb-2">No decision is recorded for this reference</h4>
          <p className="text-muted mb-0">
            {`Nothing in the decision log carries ${reference}. The policy only
            writes a row when it scores an offer, so an offer handled before
            the policy existed — or by hand, outside it — has no trace here.`}
          </p>
        </div>
      </div>
    );
  }

  const features = row.features || {};
  const headline = decisionHeadline(row);
  const resolution = resolutionBadge(row.resolution);
  const failedSteps = failedPostSteps(row);
  const docs = partnerDocsCopy(row.partner_docs);
  const outcome = outcomeSentence(row);
  const settled = settlingRule(row);
  const advisory = advisoryRules(row);
  const isHeld = row.decision === 'held';

  return (
    <div className="partner-sync auto-approval">
      <Link className="auto-approval-back" to="/partner-sync">← Back to the queue</Link>

      <div className="auto-approval-detail-head">
        <h1 className="auto-approval-detail-title">{row.reference}</h1>
        <Badge bg={headline.badge.bg} text={headline.badge.text} className="sync-status">
          {headline.badge.label}
        </Badge>
        <Badge bg={resolution.bg} text={resolution.text} className="sync-status sync-resolution">
          {resolution.label}
        </Badge>
        {failedSteps.length > 0 && <Badge bg="danger" className="sync-status">Follow-up failed</Badge>}
        <span className="auto-approval-detail-meta">
          {[
            partnerNameFor(row, partners),
            row.decided_at ? formatDateTime(row.decided_at) : null,
            `decided by ${decidedByLabel(row.decided_by)}`,
          ].filter(Boolean).join(' · ')}
        </span>
      </div>

      {sorted.length > 1 && (
        <p className="auto-approval-note-quiet">
          {`The newest of ${sorted.length} decisions recorded for this reference.
          A pass re-scores an offer nobody has acted on, so an offer that sat
          while other offers arrived carries a row per pass that looked at it.`}
        </p>
      )}

      <Error error={error} setError={setError} />

      {acted && (
        <p className="auto-approval-note-quiet">
          {acted === 'approved'
            ? 'Sent to the partner as approved. The decision log keeps the policy’s original verdict beside it — that is the disagreement, and it is the point.'
            : 'Declined on the partner. The policy’s verdict beside it is unchanged.'}
        </p>
      )}

      <div className="auto-approval-detail-body">
        <div className="auto-approval-detail-main">
          <section className="auto-approval-panel">
            <h2 className="auto-approval-panel-title">The agent&apos;s reasoning</h2>
            <p className="auto-approval-panel-blurb">
              Every check it ran, in order, with the value it saw. Nothing here
              is generated prose — each line is a rule and its result.
              {/* `settlingRule` never names an advisory rule, so "the last
                  one to clear" is always a rule that could have refused. */}
              {settled && (settled.failed
                ? ` ${prettifyToken(settled.id)} is the rule that stopped it.`
                : ` ${prettifyToken(settled.id)} was the last one that could have refused, and did not.`)}
            </p>
            {/* Said ABOVE the trace, because a reader who takes the ticks in
                first has already drawn the wrong conclusion. */}
            {advisory.length > 0 && (
              <p className="auto-approval-panel-blurb auto-approval-note-quiet">
                {ADVISORY_RULE_COPY}
              </p>
            )}
            <RuleTrace rules={row.rules_fired} />
          </section>

          {/* The policy standing down, said as itself and never as a hold.
              `decisionHeadline` owns the distinction. */}
          {headline.policyStoodDown && (
            <section className="auto-approval-panel auto-approval-panel--off">
              <h2 className="auto-approval-panel-title">
                <i className="fa fa-power-off me-2" aria-hidden="true" />
                Auto-approval was not switched on
              </h2>
              <p className="mb-0">{headline.sentence}</p>
            </section>
          )}

          {headline.showHeldReason && (
            <section className="auto-approval-panel auto-approval-panel--held">
              <h2 className="auto-approval-panel-title">Why a person has to decide this one</h2>
              <p className="mb-0">{heldReasonCopy(row.held_reason)}</p>
            </section>
          )}

          {outcome && (
            <section className="auto-approval-panel auto-approval-panel--outcome">
              <h2 className="auto-approval-panel-title">What happened instead</h2>
              <p className="mb-0">{outcome}</p>
            </section>
          )}

          {failedSteps.map((step, index) => (
            <section
              // eslint-disable-next-line react/no-array-index-key
              key={`failed-${step?.id ?? 'step'}-${index}`}
              className="auto-approval-panel auto-approval-panel--alarm"
            >
              <h2 className="auto-approval-panel-title">
                <i className="fa fa-triangle-exclamation me-2" aria-hidden="true" />
                After the decision
              </h2>
              <p className="mb-0">{postStepFailureCopy(step, partnerNameFor(row, partners))}</p>
            </section>
          ))}

          <section className="auto-approval-panel">
            <h2 className="auto-approval-panel-title">
              {isHeld ? 'What it would have cost us to be wrong' : 'The numbers it decided on'}
            </h2>
            <CostOfBeingWrong features={features} />
            <p className="auto-approval-panel-blurb mb-0">
              {'Margin '}
              <span className={`sync-profit sync-profit--${profitToneFor(features.margin)}`}>
                {formatMarginPct(features.margin)}
              </span>
              {isAbsent(features.margin)
                ? ' — no quote could be computed, so there is no margin to compare against.'
                : ' on our own price for this route.'}
            </p>
          </section>

          {/* Phase 2. Only for a decision that actually set something in
              motion — an unresolved hold has no chain, and an empty numbered
              timeline reads as a chain that failed. */}
          {Array.isArray(row.post_steps) && row.post_steps.length > 0 && (
            <section className="auto-approval-panel">
              <h2 className="auto-approval-panel-title">What approval set in motion</h2>
              <p className="auto-approval-panel-blurb">
                Partner-specific, and each step can fail by itself. The accept
                call carries the state hash from when the offer was fetched, so
                a change on the partner&apos;s side refuses it and leaves the
                booking ours and not theirs — which is why the trace does not
                stop at &quot;approved&quot;.
              </p>
              <PostSteps steps={row.post_steps} />
            </section>
          )}

          <section className="auto-approval-panel">
            <h2 className="auto-approval-panel-title">What the policy saw</h2>
            <div className="sync-meta sync-decision-features">
              <div className="sync-meta-item">
                <span className="sync-meta-label">Lead time</span>
                <span className="sync-meta-value">
                  {formatNumberOrDash(features.lead_days, (days) => `${days} days`)}
                </span>
              </div>
              <div className="sync-meta-item">
                <span className="sync-meta-label">Passengers</span>
                <span className="sync-meta-value">{dashIfBlank(features.pax)}</span>
              </div>
              <div className="sync-meta-item">
                <span className="sync-meta-label">Vehicle</span>
                <span className="sync-meta-value">{dashIfBlank(features.vehicle)}</span>
              </div>
              <div className="sync-meta-item">
                <span className="sync-meta-label">Pickup hour</span>
                <span className="sync-meta-value">
                  {formatNumberOrDash(
                    features.hour_local,
                    (hour) => `${String(hour).padStart(2, '0')}:00 local`,
                  )}
                </span>
              </div>
              <div className="sync-meta-item">
                <span className="sync-meta-label">Partner accept rate</span>
                <span className="sync-meta-value">
                  {formatFractionAsPct(features.partner_accept_rate)}
                </span>
              </div>
              <div className="sync-meta-item">
                <span className="sync-meta-label">Auto-approval</span>
                <span className="sync-meta-value">
                  {row.auto_approval_enabled === false ? 'Off for this partner' : 'On for this partner'}
                </span>
              </div>
            </div>
          </section>

          <div className="auto-approval-actions">
            <Button variant="success" disabled={!canAct} onClick={() => setShowApprove(true)}>
              {isHeld ? 'Approve anyway' : 'Approve'}
            </Button>
            <Button variant="outline-secondary" disabled={!canAct} onClick={reject}>
              Reject
            </Button>
            <Button variant="outline-secondary" onClick={() => setShowChat((open) => !open)}>
              <i className="fa fa-comments me-2" aria-hidden="true" />
              {showChat ? 'Close the agent' : 'Ask the agent'}
            </Button>
          </div>

          <p className="auto-approval-note-quiet">
            {!queueChecked && 'Checking whether this offer is still on the sync queue…'}
            {queueChecked && canAct
              && 'Approving against the policy is recorded as a disagreement. Approving opens the review form first — the same one the sync tab opens — because that is where the price and the operator get fixed.'}
            {queueChecked && !canAct && !acted
              && 'This offer is no longer on the sync queue, so it cannot be approved or declined from here. Acting on it needs the state hash the queue row carries, and that row is gone — handle it on the partner’s portal.'}
          </p>

          {showChat && (
            <section className="auto-approval-panel">
              <h2 className="auto-approval-panel-title">Ask the agent</h2>
              {/* `storageNamespace` is REQUIRED — see ChatPanel. Keyed by
                  reference so two decisions are two conversations and neither
                  reads the floating widget's session. */}
              <ChatPanel
                storageNamespace={`sync-decision-${row.reference}`}
                title="Sync decisions"
                icon="fa-scale-balanced"
                subtitle={`scoped to ${row.reference}`}
                placeholder={`Ask why ${row.reference} was decided the way it was.`}
                composerPlaceholder="Why was this held?"
                transformMessage={transformMessage}
              />
              <p className="auto-approval-panel-blurb mb-0">
                The agent can read this decision and explain it. It cannot
                approve or decline — that is the two buttons above.
              </p>
            </section>
          )}
        </div>

        <aside className="auto-approval-detail-side">
          <AgentReview review={row.review} />

          <section className="auto-approval-panel">
            <h2 className="auto-approval-panel-title">What the policy could not see</h2>
            <dl className="sync-decision-context mb-0">
              <dt>Demand for this date</dt>
              <dd>
                {row.demand_context ? JSON.stringify(row.demand_context) : DEMAND_UNKNOWN_COPY}
              </dd>
              <dt>Partner&apos;s own terms</dt>
              <dd className={docs.consulted === false ? 'sync-decision-fail-text' : undefined}>
                {docs.text}
              </dd>
              <dt>Model</dt>
              <dd>
                {row.model
                  ? String(row.model)
                  : 'None — this was decided by the rules above, not by a model.'}
              </dd>
            </dl>
          </section>

          <PolicyInForce rows={sorted} partners={partners} />
        </aside>
      </div>

      {showApprove && order && (
        <OrderInputModal
          show
          handleClose={() => setShowApprove(false)}
          originalOrder={order}
          fetchOrders={approved}
        />
      )}
    </div>
  );
}

export default DecisionDetail;
